/**
 * Where SAM's weights actually come from.
 *
 * Meta's own `facebookresearch/segment-anything` repo only ships an ONNX
 * export script for the mask decoder — not the image encoder (confirmed
 * 2026-10-01: their `scripts/export_onnx_model.py` covers the decoder only).
 * So "official, no conversion needed" in practice means the artifact
 * Microsoft's own ONNX Runtime team already converted (via `samexporter`,
 * from Meta's unmodified ViT-B checkpoint) and fetches directly from the
 * browser in their own example — `microsoft/onnxruntime-inference-examples`,
 * `js/segment-anything/index.js`, which hard-codes these same two URLs. That
 * demo is the thing this feature's SAM support is verified against: same
 * model files, same tensor names, same `caches.open("onnx")` fetch-and-cache
 * pattern.
 *
 * The hosting account (`schmuell`, an ONNX Runtime engineer) has not attached
 * a license file to the repo itself, but the weights are an unmodified format
 * conversion of Meta's ViT-B SAM checkpoint, which Meta releases under
 * Apache-2.0 — a conversion does not create a new restriction on top of that.
 */
export interface SamModelPart {
  name: string;
  url: string;
  approxMB: number;
}

// Sizes measured directly (a ranged GET against each URL, 2026-10-01), not
// copied from the Microsoft sample's own (slightly rounder) numbers.
export const SAM_ENCODER: SamModelPart = {
  name: "SAM ViT-B encoder (int8)",
  url: "https://huggingface.co/schmuell/sam-b-fp16/resolve/main/sam_vit_b-encoder-int8.onnx",
  approxMB: 96,
};

export const SAM_DECODER: SamModelPart = {
  name: "SAM ViT-B decoder (int8)",
  url: "https://huggingface.co/schmuell/sam-b-fp16/resolve/main/sam_vit_b-decoder-int8.onnx",
  approxMB: 4.5,
};

export const SAM_TOTAL_MB = SAM_ENCODER.approxMB + SAM_DECODER.approxMB;

export const SAM_LICENSE_NOTE =
  "Meta's Segment Anything (ViT-B) checkpoint, Apache License 2.0, exported to ONNX by the ONNX Runtime team " +
  "(no re-training, no modification — int8 quantized for WebAssembly). Downloaded directly from Hugging Face " +
  "to your browser and cached there; OpenADS Workstation never sees the image you run it on.";

/** `caches.open(...)` key — kept distinct from other models' fetch caches. */
export const SAM_CACHE_NAME = "ads-desk-sam-onnx";
